import { useNavigate } from 'react-router-dom'

import { RoundBadge } from '../components/rounds.tsx'
import { Badge, Card, CardBody, CardHead, LoadError, PageHead, Skeleton } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { moneyIn, pnlClass } from '../lib/format.ts'
import type { LeaderboardRow } from '../../shared/types.ts'

const pct = (r: number) => `${r >= 1 ? '+' : '−'}${Math.abs((r - 1) * 100).toFixed(1)}%`

/**
 * Every player, ranked by how their current round is doing against its 10,000 start —
 * a share rather than an amount, so GBP, USD and EUR rounds compare fairly. How many of
 * their rounds went to zero is on the record for everyone.
 */
export function Leaderboard() {
  usePageTitle('Leaderboard')
  const { data, error, loading, reload } = useApi<{ rows: LeaderboardRow[] }>('/leaderboard', 60_000)
  const navigate = useNavigate()

  return (
    <main className="content">
      <PageHead title="Leaderboard" sub="Every player's current round, and how many of their rounds went to zero." />

      {error ? (
        <LoadError error={error} onRetry={reload} />
      ) : loading || !data ? (
        <Card>
          <CardBody>
            <Skeleton rows={8} />
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHead title="Players" sub={`${data.rows.length} playing`} />
          <CardBody flush>
            <div className="table-wrap">
              <table className="tbl rows-clickable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Round</th>
                    <th className="num">Balance</th>
                    <th className="num">Return</th>
                    <th className="num">Rounds</th>
                    <th className="num">Went to zero</th>
                    <th className="num">Best round</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const open = () => navigate(`/players/${r.playerId}`)
                    const ret = r.current.balance / r.current.startingBalance
                    return (
                      <tr
                        key={r.playerId}
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') open()
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open ${r.name}`}
                      >
                        <td className="u-mono">{i + 1}</td>
                        <td className="cell-strong">
                          {r.name} {r.you && <Badge tone="teal">You</Badge>}
                        </td>
                        <td>
                          {r.current.number} <RoundBadge status={r.current.status} />
                        </td>
                        <td className="num u-mono">{moneyIn(r.current.balance, r.current.currency)}</td>
                        <td className={`num u-mono ${pnlClass(ret - 1)}`}>{pct(ret)}</td>
                        <td className="num u-mono">{r.rounds}</td>
                        <td className={`num u-mono${r.busts ? ' neg' : ''}`}>{r.busts}</td>
                        <td className={`num u-mono ${pnlClass(r.bestReturn - 1)}`}>{pct(r.bestReturn)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </main>
  )
}
